import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Loader2, RefreshCw } from 'lucide-react';
import { useCheckout, SUBSCRIPTION_STATUS } from "../CheckoutContext";
import { useSubscriptionPolling } from '../hooks/useSubscriptionPolling';
import { setSubscriptionData, selectIsSubscriptionActive, selectSubscription } from '../../../Redux/subscriptionSlice';
import Modal from "../../shared/Modal";
import OrderSummary from "./OrderSummary";
import PaymentMethodSelector from "./PaymentMethodSelector";
import CheckoutBillingForm from "./CheckoutBillingForm";

const CheckoutModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isSubscriptionActive = useSelector(selectIsSubscriptionActive);
  const subscription = useSelector(selectSubscription);
  const { open, closeCheckout, plan, cycle, subscriptionStatus, setSubscriptionStatus } = useCheckout();

  // When subscription becomes active (non-basic), close modal and go to marketplace (e.g. after refresh).
  useEffect(() => {
    if (open && isSubscriptionActive) {
      closeCheckout();
      navigate('/marketplace', { replace: true });
    }
  }, [open, isSubscriptionActive, closeCheckout, navigate]);

  const handleSubscriptionActivated = useCallback(
    (subscription) => {
      // Close first so polling stops and modal disappears immediately
      closeCheckout();
      setSubscriptionStatus(SUBSCRIPTION_STATUS.IDLE);
      if (subscription) {
        dispatch(setSubscriptionData(subscription));
      }
      toast.success('Subscription activated successfully.');
      navigate('/marketplace', { replace: true });
    },
    [dispatch, navigate, setSubscriptionStatus, closeCheckout]
  );

  const handleSubscriptionTimeout = useCallback(() => {
    setSubscriptionStatus(SUBSCRIPTION_STATUS.TIMEOUT);
  }, [setSubscriptionStatus]);

  useSubscriptionPolling({
    enabled: open && subscriptionStatus === SUBSCRIPTION_STATUS.PROCESSING,
    onActivated: handleSubscriptionActivated,
    onTimeout: handleSubscriptionTimeout,
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
  });
  const [errors, setErrors] = useState({});

  if (!open || !plan) return null;

  const isProcessing = subscriptionStatus === SUBSCRIPTION_STATUS.PROCESSING;
  const isTimeout = subscriptionStatus === SUBSCRIPTION_STATUS.TIMEOUT;

  if (isProcessing) {
    return (
      <Modal onClose={() => {}} size="xl">
        <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
          <Loader2 className="w-12 h-12 text-black animate-spin mb-4" aria-hidden />
          <h2 className="text-xl font-semibold mb-2">Processing your subscription...</h2>
          <p className="text-gray-600">
            We are activating your plan. This usually takes a few seconds.
          </p>
        </div>
      </Modal>
    );
  }

  if (isTimeout) {
    return (
      <Modal onClose={closeCheckout} size="xl">
        <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
          <p className="text-gray-700 mb-6">
            Payment processing is taking longer than expected. Please refresh the page to see your updated subscription.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition"
          >
            <RefreshCw size={18} />
            Refresh
          </button>
          <button
            type="button"
            onClick={closeCheckout}
            className="mt-4 text-gray-600 hover:text-gray-800 underline text-sm"
          >
            Close
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={closeCheckout} size="xl">
      <h2 className="text-xl font-semibold mb-6">
        Upgrade to {plan.name}
      </h2>

      {/* Responsive Layout */}
      {subscription?.paymentRecoveryRequired && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          Your previous auto-debit failed and Razorpay marked this subscription as
          {' '}
          <span className="font-semibold">{subscription?.paymentRecoveryState || 'pending'}</span>.
          Use the button below to update card/UPI and resume billing.
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-8">

        {/* LEFT SIDE - Billing + Terms */}
        <div className="md:w-1/2">
          <CheckoutBillingForm
            plan={plan}
            cycle={cycle}
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
          />
        </div>

        {/* RIGHT SIDE - Summary + Payment */}
        <div className="md:w-1/2 space-y-6">

          <div className="rounded-xl border p-4 bg-gray-50">
            <OrderSummary plan={plan} cycle={cycle} />
          </div>

          <div className="rounded-xl border p-4">
            <PaymentMethodSelector
              plan={plan}
              cycle={cycle}
              billingFormData={formData}
              billingErrors={errors}
              setBillingErrors={setErrors}
            />
          </div>

        </div>

      </div>
    </Modal>
  );
};

export default CheckoutModal;
